import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import {
  Briefcase, Users, Eye, TrendingUp, CheckCircle, Clock, XCircle, 
  UserCheck, FileText, Mail, Linkedin, ExternalLink, Download, 
  BarChart3, Plus, RefreshCw, Building2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: "bg-gray-500",
  reviewing: "bg-blue-500",
  shortlisted: "bg-green-500",
  rejected: "bg-red-500",
  hired: "bg-emerald-600"
};

const STATUS_ICONS = {
  pending: Clock,
  reviewing: Eye,
  shortlisted: UserCheck,
  rejected: XCircle,
  hired: CheckCircle
};

const CompanyJobDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAuthenticated, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [myJobs, setMyJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [viewApplications, setViewApplications] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchDashboard();
      fetchMyJobs();
    }
  }, [isAuthenticated, token]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/jobs/company-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (e) {
      console.error("Dashboard fetch error:", e);
    }
  };

  const fetchMyJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs/my-postings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyJobs(res.data.jobs || []);
    } catch (e) {
      console.error("Jobs fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (applicationId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/jobs/applications/${applicationId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: "Status Updated", description: `Application marked as ${newStatus}` });
      fetchMyJobs();
      fetchDashboard();
    } catch (e) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="container mx-auto px-6 py-12 text-center">
          <h1 className="text-2xl text-white mb-4">Please Sign In</h1>
          <Button onClick={() => navigate('/auth')}>Sign In to View Dashboard</Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  const metrics = dashboard?.metrics || {};
  const statusBreakdown = dashboard?.status_breakdown || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="h-8 w-8" />
                <h1 className="text-3xl font-bold">Company Recruiting Dashboard</h1>
              </div>
              <p className="opacity-90">Manage your job postings and review applicants</p>
            </div>
            <Button onClick={() => navigate('/jobs')} className="bg-white text-blue-600 hover:bg-gray-100">
              <Plus className="h-4 w-4 mr-2" /> Post New Job
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Metrics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-gray-800/50 border-gray-700" data-testid="metric-total-jobs">
            <CardContent className="p-4 text-center">
              <Briefcase className="h-8 w-8 text-blue-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{metrics.total_jobs || 0}</p>
              <p className="text-gray-400 text-sm">Total Jobs</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700" data-testid="metric-active-jobs">
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{metrics.active_jobs || 0}</p>
              <p className="text-gray-400 text-sm">Active Jobs</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700" data-testid="metric-views">
            <CardContent className="p-4 text-center">
              <Eye className="h-8 w-8 text-purple-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{metrics.total_views || 0}</p>
              <p className="text-gray-400 text-sm">Total Views</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700" data-testid="metric-applications">
            <CardContent className="p-4 text-center">
              <Users className="h-8 w-8 text-orange-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{metrics.total_applications || 0}</p>
              <p className="text-gray-400 text-sm">Applications</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700" data-testid="metric-rate">
            <CardContent className="p-4 text-center">
              <TrendingUp className="h-8 w-8 text-cyan-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{metrics.application_rate || 0}%</p>
              <p className="text-gray-400 text-sm">Apply Rate</p>
            </CardContent>
          </Card>
        </div>

        {/* Application Status Breakdown */}
        <Card className="bg-gray-800/50 border-gray-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="h-5 w-5" /> Application Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-4">
              {Object.entries(statusBreakdown).map(([status, count]) => {
                const StatusIcon = STATUS_ICONS[status] || Clock;
                return (
                  <div key={status} className="text-center">
                    <div className={`${STATUS_COLORS[status]} w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <StatusIcon className="h-6 w-6 text-white" />
                    </div>
                    <p className="text-2xl font-bold text-white">{count}</p>
                    <p className="text-gray-400 text-sm capitalize">{status}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="jobs" className="space-y-6">
          <TabsList className="bg-gray-800/50 border border-gray-700">
            <TabsTrigger value="jobs" className="data-[state=active]:bg-blue-600">My Job Postings</TabsTrigger>
            <TabsTrigger value="recent" className="data-[state=active]:bg-purple-600">Recent Applications</TabsTrigger>
          </TabsList>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            {myJobs.length === 0 ? (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardContent className="py-12 text-center">
                  <Briefcase className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-xl text-white mb-2">No Jobs Posted Yet</h3>
                  <p className="text-gray-400 mb-4">Start attracting talent by posting your first job</p>
                  <Button onClick={() => navigate('/jobs')} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="h-4 w-4 mr-2" /> Post a Job
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myJobs.map(job => (
                  <Card key={job.id} className="bg-gray-800/50 border-gray-700 hover:border-blue-500/50 transition-colors" data-testid={`job-card-${job.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-semibold text-white">{job.title}</h3>
                            <Badge className={job.is_active ? "bg-green-600" : "bg-gray-600"}>
                              {job.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <p className="text-gray-400 text-sm mb-3">{job.location} • {job.job_type}</p>
                          
                          {/* Stats Row */}
                          <div className="flex items-center gap-6 text-sm">
                            <span className="flex items-center gap-1 text-gray-300">
                              <Eye className="h-4 w-4" /> {job.views_count || 0} views
                            </span>
                            <span className="flex items-center gap-1 text-gray-300">
                              <Users className="h-4 w-4" /> {job.applications_count || 0} applications
                            </span>
                          </div>

                          {/* Application Stats */}
                          {job.application_stats && (
                            <div className="flex items-center gap-3 mt-3">
                              {Object.entries(job.application_stats).filter(([k, v]) => v > 0 && k !== 'total').map(([status, count]) => (
                                <Badge key={status} variant="outline" className={`${STATUS_COLORS[status]} text-white border-0`}>
                                  {count} {status}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedJob(job); setViewApplications(true); }}
                            className="border-blue-500 text-blue-400 hover:bg-blue-500/20"
                          >
                            <Users className="h-4 w-4 mr-1" /> View Applicants
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Recent Applications Tab */}
          <TabsContent value="recent">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Recent Applications</CardTitle>
                <CardDescription>Latest candidates who applied to your jobs</CardDescription>
              </CardHeader>
              <CardContent>
                {(dashboard?.recent_applications || []).length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No applications yet</p>
                ) : (
                  <div className="space-y-3">
                    {dashboard.recent_applications.map(app => (
                      <div key={app.id} className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg" data-testid={`application-${app.id}`}>
                        <div>
                          <p className="text-white font-medium">{app.applicant_name}</p>
                          <p className="text-gray-400 text-sm">{app.applicant_email}</p>
                          <p className="text-gray-500 text-xs mt-1">Applied for: {app.job_title}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {app.linkedin_url && (
                            <a href={app.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                              <Linkedin className="h-5 w-5" />
                            </a>
                          )}
                          {app.cv_url && (
                            <a href={app.cv_url} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300">
                              <Download className="h-5 w-5" />
                            </a>
                          )}
                          <Badge className={STATUS_COLORS[app.status || 'pending']}>{app.status || 'pending'}</Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedApplication(app)}
                          >
                            Review
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* View Applications Modal */}
      <Dialog open={viewApplications} onOpenChange={setViewApplications}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              Applications for {selectedJob?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {(selectedJob?.applications || []).length === 0 ? (
              <p className="text-gray-400 text-center py-8">No applications yet</p>
            ) : (
              selectedJob.applications.map(app => (
                <Card key={app.id} className="bg-gray-900/50 border-gray-600">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-white font-medium">{app.applicant_name}</h4>
                          <Badge className={STATUS_COLORS[app.status || 'pending']}>{app.status || 'pending'}</Badge>
                        </div>
                        <p className="text-gray-400 text-sm flex items-center gap-1">
                          <Mail className="h-4 w-4" /> {app.applicant_email}
                        </p>
                        {app.cover_letter_text && (
                          <p className="text-gray-300 text-sm mt-2 line-clamp-2">{app.cover_letter_text}</p>
                        )}
                        <div className="flex items-center gap-3 mt-3">
                          {app.cv_url && (
                            <a href={app.cv_url} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1">
                              <FileText className="h-4 w-4" /> View CV
                            </a>
                          )}
                          {app.linkedin_url && (
                            <a href={app.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1">
                              <Linkedin className="h-4 w-4" /> LinkedIn
                            </a>
                          )}
                        </div>
                      </div>
                      <Select
                        defaultValue={app.status || 'pending'}
                        onValueChange={(val) => updateApplicationStatus(app.id, val)}
                        disabled={updatingStatus}
                      >
                        <SelectTrigger className="w-32 bg-gray-700 border-gray-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="reviewing">Reviewing</SelectItem>
                          <SelectItem value="shortlisted">Shortlisted</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                          <SelectItem value="hired">Hired</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Single Application Review Modal */}
      <Dialog open={!!selectedApplication} onOpenChange={() => setSelectedApplication(null)}>
        <DialogContent className="bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Review Application</DialogTitle>
          </DialogHeader>
          {selectedApplication && (
            <div className="space-y-4">
              <div>
                <p className="text-gray-400 text-sm">Applicant</p>
                <p className="text-white text-lg font-medium">{selectedApplication.applicant_name}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Email</p>
                <p className="text-white">{selectedApplication.applicant_email}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Position</p>
                <p className="text-white">{selectedApplication.job_title}</p>
              </div>
              {selectedApplication.cover_letter_text && (
                <div>
                  <p className="text-gray-400 text-sm">Cover Letter</p>
                  <p className="text-gray-300 text-sm bg-gray-900 p-3 rounded">{selectedApplication.cover_letter_text}</p>
                </div>
              )}
              <div className="flex items-center gap-3">
                {selectedApplication.cv_url && (
                  <Button variant="outline" asChild className="border-green-500 text-green-400">
                    <a href={selectedApplication.cv_url} target="_blank" rel="noopener noreferrer">
                      <Download className="h-4 w-4 mr-2" /> Download CV
                    </a>
                  </Button>
                )}
                {selectedApplication.linkedin_url && (
                  <Button variant="outline" asChild className="border-blue-500 text-blue-400">
                    <a href={selectedApplication.linkedin_url} target="_blank" rel="noopener noreferrer">
                      <Linkedin className="h-4 w-4 mr-2" /> LinkedIn
                    </a>
                  </Button>
                )}
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-2">Update Status</p>
                <Select
                  defaultValue={selectedApplication.status || 'pending'}
                  onValueChange={(val) => {
                    updateApplicationStatus(selectedApplication.id, val);
                    setSelectedApplication(null);
                  }}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewing">Reviewing</SelectItem>
                    <SelectItem value="shortlisted">Shortlisted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="hired">Hired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyJobDashboard;
